import { useEffect, useMemo } from 'react'
import {Building2, Calendar, Star, Briefcase, Clock, CheckCircle2, History} from 'lucide-react'
import ColaboradorLayout from '@/components/ColaboradorLayout'
import { useLinks, type WorkLink } from '@/hooks/useLinks'
import { useEvaluations, type Evaluation } from '@/hooks/useEvaluations'
import { useAuth } from '@/hooks/useAuth'

const STATUS_LABELS: Record<WorkLink['status'], string> = {
  pendiente: 'Pendiente',
  aceptada: 'Activa',
  rechazada: 'Rechazada',
  finalizada: 'Finalizada',
}

const STATUS_STYLES: Record<WorkLink['status'], string> = {
  pendiente: 'bg-amber-50 text-amber-700',
  aceptada: 'bg-emerald-50 text-emerald-700',
  rechazada: 'bg-red-50 text-red-700',
  finalizada: 'bg-slate-100 text-slate-600',
}

function scoreColor(value: number) {
  if (value >= 8) return 'text-emerald-600'
  if (value >= 6) return 'text-amber-600'
  return 'text-red-600'
}

function formatDate(value?: string) {
  if (!value) return null
  try {
    return new Date(value).toLocaleDateString('es-MX', { year: 'numeric', month: 'short' })
  } catch {
    return value
  }
}

// Antiguedad (en meses) entre dos fechas; si no hay fin, hasta hoy.
function monthsBetween(start?: string, end?: string): number {
  if (!start) return 0
  const s = new Date(start)
  const e = end ? new Date(end) : new Date()
  if (isNaN(s.getTime())) return 0
  const months = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth())
  return Math.max(0, months)
}

function formatSeniority(totalMonths: number): string {
  if (totalMonths <= 0) return '0 meses'
  const years = Math.floor(totalMonths / 12)
  const months = totalMonths % 12
  const parts: string[] = []
  if (years > 0) parts.push(`${years} ${years === 1 ? 'año' : 'años'}`)
  if (months > 0) parts.push(`${months} ${months === 1 ? 'mes' : 'meses'}`)
  return parts.join(' ') || '0 meses'
}

interface ExperienceItem {
  link: WorkLink
  evaluations: Evaluation[]
  evalAverage: number
  months: number
}

function ExperienceCard({ item }: { item: ExperienceItem }) {
  const { link, evaluations, evalAverage, months } = item
  return (
    <div className="relative rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
            <Building2 size={20} />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-900">{link.companyName || 'Empresa'}</p>
            <p className="text-xs text-slate-500">{link.position || 'Colaborador'}{link.area ? ` · ${link.area}` : ''}</p>
          </div>
        </div>
        <span className={`rounded-full px-3 py-1 text-xs font-medium ${STATUS_STYLES[link.status]}`}>
          {STATUS_LABELS[link.status]}
        </span>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <Calendar size={14} className="text-slate-400" />
          <span>
            {formatDate(link.startDate) || '—'}
            {link.status === 'finalizada' ? ` — ${formatDate(link.endDate) || '—'}` : link.status === 'aceptada' ? ' — Actual' : ''}
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <Clock size={14} className="text-slate-400" />
          <span>{formatSeniority(months)}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <Star size={14} className="text-amber-500" />
          <span>
            {evaluations.length > 0 ? (
              <>
                <span className={`font-semibold ${scoreColor(evalAverage)}`}>{evalAverage.toFixed(1)}</span> · {evaluations.length} {evaluations.length === 1 ? 'evaluación' : 'evaluaciones'}
              </>
            ) : (
              'Sin evaluaciones'
            )}
          </span>
        </div>
      </div>
    </div>
  )
}

function ColaboradorHistorial() {
  const { user } = useAuth()
  const { links, loading: linksLoading, fetchByCollaborator: fetchLinks } = useLinks()
  const { evaluations, loading: evalsLoading, fetchByCollaborator: fetchEvals } = useEvaluations()

  useEffect(() => {
    if (user?.userId) {
      // Cargas en paralelo: vinculaciones y evaluaciones
      fetchLinks(user.userId)
      fetchEvals(user.userId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId])

  const loading = linksLoading || evalsLoading

  // Construye la experiencia laboral a partir de vinculos relevantes (no pendientes/rechazados)
  const experiences = useMemo<ExperienceItem[]>(() => {
    const relevant = links.filter((l) => l.status === 'aceptada' || l.status === 'finalizada')
    return relevant.map((link) => {
      const evs = evaluations.filter((e) => e.companyUserId === link.companyUserId)
      const evalAverage =
        evs.length > 0 ? Math.round((evs.reduce((acc, e) => acc + (e.average || 0), 0) / evs.length) * 10) / 10 : 0
      return {
        link,
        evaluations: evs,
        evalAverage,
        months: monthsBetween(link.startDate, link.status === 'finalizada' ? link.endDate : undefined),
      }
    })
  }, [links, evaluations])

  const stats = useMemo(() => {
    const companies = new Set(experiences.map((e) => e.link.companyUserId))
    const totalMonths = experiences.reduce((acc, e) => acc + e.months, 0)
    const globalAvg =
      evaluations.length > 0
        ? Math.round((evaluations.reduce((acc, e) => acc + (e.average || 0), 0) / evaluations.length) * 10) / 10
        : 0
    const active = experiences.filter((e) => e.link.status === 'aceptada').length
    return {
      companies: companies.size,
      seniority: formatSeniority(totalMonths),
      globalAvg,
      active,
    }
  }, [experiences, evaluations])

  return (
    <ColaboradorLayout
      title="Historial laboral"
      subtitle="Tu trayectoria verificable en Beeznz Work: empresas, periodos y evaluaciones recibidas."
    >
      {loading ? (
        <div className="py-20 text-center text-sm text-slate-500">Cargando historial...</div>
      ) : experiences.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center">
          <History size={40} className="mx-auto text-slate-300" />
          <p className="mt-4 text-sm font-medium text-slate-700">Aún no tienes historial laboral</p>
          <p className="mt-1 text-sm text-slate-500">
            Cuando una empresa acepte tu vinculación, tu experiencia y evaluaciones aparecerán aquí de forma verificable.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="flex items-center gap-2 text-slate-500">
                <Building2 size={16} className="text-amber-500" />
                <span className="text-xs font-medium">Empresas vinculadas</span>
              </div>
              <p className="mt-2 text-3xl font-bold text-slate-900">{stats.companies}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="flex items-center gap-2 text-slate-500">
                <Clock size={16} className="text-amber-500" />
                <span className="text-xs font-medium">Antigüedad acumulada</span>
              </div>
              <p className="mt-2 text-lg font-bold text-slate-900">{stats.seniority}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="flex items-center gap-2 text-slate-500">
                <Star size={16} className="text-amber-500" />
                <span className="text-xs font-medium">Evaluación promedio</span>
              </div>
              <p className={`mt-2 text-3xl font-bold ${scoreColor(stats.globalAvg)}`}>
                {stats.globalAvg > 0 ? stats.globalAvg.toFixed(1) : '—'}
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="flex items-center gap-2 text-slate-500">
                <CheckCircle2 size={16} className="text-amber-500" />
                <span className="text-xs font-medium">Vínculos activos</span>
              </div>
              <p className="mt-2 text-3xl font-bold text-slate-900">{stats.active}</p>
            </div>
          </div>

          <div>
            <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700">
              <Briefcase size={15} className="text-amber-500" /> Experiencia laboral
            </h2>
            <div className="space-y-3">
              {experiences.map((item) => (
                <ExperienceCard key={item.link._id} item={item} />
              ))}
            </div>
          </div>
        </div>
      )}
    </ColaboradorLayout>
  )
}

export default ColaboradorHistorial
